import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { NewsArticleNumList } from '@/features/actualidad-hostelera/NewsArticleNumList'
import { createNewsArticleSourceChip } from '@/features/actualidad-hostelera/NewsArticleSourceChip'
import type { NewsArticleBodyProps } from '@/features/actualidad-hostelera/articles'
import { learnArticlePath } from '@/features/aprender/articles'
import { toOfficialSalesWhatsAppHref } from '@/lib/officialContact'
import { cn } from '@/lib/utils'
import { LocaleLink } from '@/navigation/LocaleLink'
import { article } from '@/shared/lib/articleStyles'
import { ArticleByline } from '@/shared/ui/ArticleByline'
import { ArticleCoverHero } from '@/shared/ui/ArticleCoverHero'
import { ArticleReadingLayout } from '@/shared/ui/ArticleReadingLayout'
import { ArticleToc } from '@/shared/ui/ArticleToc'
import '@/styles/food-cost-fonts.css'

const ARTICLE_ID = 'verifactu-retraso-2028-hosteleria'
const k = (f: string) => `news.articles.${ARTICLE_ID}.${f}`

const REF = {
    efe: {
        href: 'https://www.infobae.com/espana/agencias/2026/10/05/hacienda-retrasa-verifactu-hasta-2028-para-alinearlo-con-la-factura-electronica/',
        domain: 'infobae.com',
        label: 'EFE · Infobae',
    },
    eleconomista: {
        href: 'https://www.eleconomista.es/economia/noticias/14018805/10/26/hacienda-vuelve-a-retrasar-la-entrada-en-vigor-de-verifactu-hasta-octubre-de-2028.html',
        domain: 'eleconomista.es',
        label: 'elEconomista',
    },
    elespanol: {
        href: 'https://www.elespanol.com/invertia/economia/20261005/hacienda-vuelve-retrasar-verifactu-pospone-implantacion-empresas-autonomos/1003744409037_0.html',
        domain: 'elespanol.com',
        label: 'El Español',
    },
    rd1007: {
        href: 'https://www.boe.es/buscar/act.php?id=BOE-A-2023-24840',
        domain: 'boe.es',
        label: 'BOE · RD 1007/2023',
    },
    rd254: {
        href: 'https://www.boe.es/buscar/doc.php?id=BOE-A-2025-6600',
        domain: 'boe.es',
        label: 'BOE · RD 254/2025',
    },
    rdl15: {
        href: 'https://www.boe.es/diario_boe/txt.php?id=BOE-A-2025-24446',
        domain: 'boe.es',
        label: 'BOE · RDL 15/2025',
    },
    lgt: {
        href: 'https://www.boe.es/buscar/act.php?id=BOE-A-2003-23186',
        domain: 'boe.es',
        label: 'BOE · LGT art. 201 bis',
    },
    marinaPlaza: {
        href: 'https://lamarina.eldiario.es/2026/09/06/la-hosteleria-de-la-marina-alta-alerta-de-un-notable-descenso-de-la-rentabilidad-de-bares-y-restaurantes-en-los-ultimos-anos/',
        domain: 'lamarina.eldiario.es',
        label: 'La Marina Plaza · AEHTMA',
    },
} as const

const SourceChip = createNewsArticleSourceChip(REF, k)

export function VerifactuRetrasoArticle({ article: meta }: NewsArticleBodyProps) {
    const { t } = useTranslation()

    const tocItems = [
        { id: 'te-llamo-el-del-tpv', label: t(k('toc.llamada'), 'Te llamó el del TPV en septiembre') },
        { id: 'que-ha-anunciado-hacienda', label: t(k('toc.hacienda'), 'Qué ha anunciado Hacienda') },
        { id: 'verifactu-ya-no-es-obligatorio', label: t(k('toc.obligatorio'), '¿Verifactu ya no es obligatorio?') },
        {
            id: 'cualquier-tpv-ya-trae-verifactu',
            label: t(k('toc.compra'), 'Cualquier TPV que compres hoy ya trae Verifactu'),
        },
        { id: 'cuanto-me-ahorro', label: t(k('toc.ahorro'), '¿Cuánto me ahorro si no cambio de TPV ahora?') },
        { id: 'lo-que-ningun-tpv-te-dice', label: t(k('toc.tpv'), 'Lo que ningún TPV te dice') },
        { id: 'lo-que-cuesta-tipi', label: t(k('toc.tipi'), 'Lo que cuesta Tipi, con todos los números') },
        {
            id: 'antes-de-que-vuelva-a-llamar',
            label: t(k('toc.comercial'), 'Antes de que vuelva a llamar el comercial'),
        },
        { id: 'lo-que-falta-por-escribir-en-el-boe', label: t(k('toc.boe'), 'Lo que falta por escribir en el BOE') },
    ]

    const contactWhatsapp = toOfficialSalesWhatsAppHref(
        t(
            k('contactWhatsapp'),
            'Hola, he leído el artículo del retraso de Verifactu y quiero que le pongáis números a lo que me cuesta cada plato.',
        ),
    )

    const checklist: ReactNode[] = [
        <>
            <strong>
                {t(
                    k('checklist.1.strong'),
                    'Pregunta por escrito a tu proveedor si tu TPV actual cumple la Ley Antifraude:',
                )}
            </strong>{' '}
            {t(
                k('checklist.1.after'),
                'que no permita borrar ni modificar un ticket cobrado sin dejar rastro. Esa respuesta decide si puedes esperar.',
            )}
        </>,
        <>
            <strong>
                {t(
                    k('checklist.2.strong'),
                    'Si firmaste un cambio por Verifactu y aún no está instalado, revisa la permanencia.',
                )}
            </strong>{' '}
            {t(k('checklist.2.after'), 'Pregunta si puedes retrasar la puesta en marcha sin penalización.')}
        </>,
        <>
            <strong>{t(k('checklist.3.strong'), 'Cuenta cuántos albaranes recibes en un mes.')}</strong>{' '}
            {t(
                k('checklist.3.after'),
                'Te dice cuánto te costaría controlar tus costes, con Tipi o con cualquier otra herramienta.',
            )}
        </>,
        <>
            <strong>
                {t(
                    k('checklist.4.strong'),
                    'Calcula el coste de tus tres platos más vendidos con los precios del último albarán.',
                )}
            </strong>{' '}
            {t(k('checklist.4.after'), 'Si te sale distinto de lo que creías, ya sabes dónde mirar primero.')}
        </>,
    ]

    const sources = [
        {
            label: t(
                k('sources.1'),
                'EFE, Hacienda retrasa Verifactu hasta 2028 para alinearlo con la factura electrónica, 5 de octubre de 2026',
            ),
            href: REF.efe.href,
        },
        {
            label: t(
                k('sources.2'),
                'elEconomista, Hacienda vuelve a retrasar la entrada en vigor de Verifactu hasta octubre de 2028, 5 de octubre de 2026',
            ),
            href: REF.eleconomista.href,
        },
        {
            label: t(
                k('sources.3'),
                'El Español, contenido de la nota informativa del Ministerio de Hacienda, 5 de octubre de 2026',
            ),
            href: REF.elespanol.href,
        },
        {
            label: t(
                k('sources.4'),
                'Real Decreto 1007/2023, de 5 de diciembre, reglamento de sistemas informáticos de facturación, texto consolidado',
            ),
            href: REF.rd1007.href,
        },
        {
            label: t(k('sources.5'), 'Real Decreto 254/2025, de 1 de abril, primera ampliación de plazos'),
            href: REF.rd254.href,
        },
        {
            label: t(k('sources.6'), 'Real Decreto-ley 15/2025, de 2 de diciembre, aplazamiento a 2027'),
            href: REF.rdl15.href,
        },
        {
            label: t(k('sources.7'), 'Ley 58/2003, General Tributaria, artículo 201 bis'),
            href: REF.lgt.href,
        },
        {
            label: t(
                k('sources.8'),
                'La Marina Plaza, declaraciones de AEHTMA sobre la rentabilidad de bares y restaurantes, 6 de septiembre de 2026',
            ),
            href: REF.marinaPlaza.href,
        },
    ]

    const ownSources = [
        t(
            k('sources.9'),
            'Asociación Española de Asesores Fiscales (AEDAF), nota de prensa sobre el aplazamiento, 5 de octubre de 2026',
        ),
        t(
            k('sources.10'),
            'Convocatoria de elecciones generales para el 29 de noviembre de 2026, anunciada el 5 de octubre de 2026',
        ),
        t(
            k('sources.11'),
            'Tarifas de software TPV para hostelería: páginas públicas de precios de Cegid Revo, Ágora, Foodtic, TPVTop y Square, y comparativas sectoriales de precios de licencia, consultadas el 6 de octubre de 2026',
        ),
        t(k('sources.12'), 'Precios de Tipi: tarifa vigente para clientes nuevos'),
        t(
            k('sources.13'),
            'Supuestos de cambio de TPV, de food cost y de consumo de albaranes: elaboración propia de Tipi',
        ),
    ]

    return (
        <div className={article.page}>
            <article className={article.shell}>
                <ArticleCoverHero
                    category={meta.category}
                    title={meta.title}
                    dek={
                        <>
                            {t(
                                k('dek.before'),
                                'Hacienda anunció el 5 de octubre de 2026 que retrasa Verifactu hasta ',
                            )}
                            <strong>{t(k('dek.date'), 'octubre de 2028')}</strong>
                            {t(
                                k('dek.after'),
                                '. Las sociedades tenían que estar adaptadas el 1 de enero de 2027, y el resto de autónomos y empresas, el 1 de julio de 2027. El retraso de Verifactu todavía no está en el BOE: es una nota informativa del Ministerio de Hacienda. Y no toca la Ley Antifraude de 2021, que sanciona con hasta 50.000 euros por ejercicio tener un programa que permita ocultar ventas. Si ibas a cambiar de TPV este otoño solo por Verifactu, ya no tienes prisa.',
                            )}
                        </>
                    }
                    imageSrc={meta.imagePath}
                    imageAlt={t(
                        k('heroAlt'),
                        'TPV en la barra de un bar, con tickets y papeles de facturación al lado',
                    )}
                    byline={
                        <ArticleByline
                            authorName={meta.authorName}
                            readingMinutes={meta.readingMinutes}
                            bylineBy={t(k('bylineBy'), 'Por')}
                        />
                    }
                />

                <ArticleReadingLayout
                    intro={
                        <div className={article.prose}>
                            <h2 id='te-llamo-el-del-tpv' className={cn(article.h2, 'mt-0')}>
                                {t(k('h2.llamada'), 'Te llamó el del TPV en septiembre')}
                            </h2>
                            <p className={article.p}>
                                {t(
                                    k('llamada.p1'),
                                    'Te lo dijo con el calendario en la mano: el 1 de enero las sociedades tenían que tener el programa adaptado, y en diciembre no habría técnicos para todos.',
                                )}
                            </p>
                            <p className={article.p}>
                                {t(k('llamada.p2'), 'Y el tuyo, el de toda la vida, no servía.')}
                            </p>
                            <p className={article.p}>
                                {t(k('llamada.p3'), 'Puede que firmaras. Puede que lo dejaras para noviembre.')}
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                {t(
                                    k('llamada.p4'),
                                    'Desde el 5 de octubre de 2026, esa llamada ya no tiene fecha detrás.',
                                )}
                            </p>
                        </div>
                    }
                    toc={
                        <ArticleToc
                            items={tocItems}
                            label={t(k('tocLabel'), 'En este artículo')}
                            ariaLabel={t(k('tocAria'), 'Índice de contenidos')}
                        />
                    }
                >
                    <section id='que-ha-anunciado-hacienda'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.hacienda'), 'Qué ha anunciado Hacienda con el retraso de Verifactu')}
                        </h2>
                        <div className={article.prose}>
                            <p className={article.p}>
                                {t(
                                    k('hacienda.p1'),
                                    'Verifactu es el sistema que obliga a que cada ticket o factura genere un registro que no se puede borrar ni modificar, y que se puede enviar a la Agencia Tributaria en el momento.',
                                )}
                            </p>
                        </div>
                        <div className={article.tableWrap}>
                            <table className={article.tableFacts}>
                                <thead>
                                    <tr>
                                        <th scope='col'>{t(k('table.who'), 'Quién')}</th>
                                        <th scope='col'>{t(k('table.when'), 'Fecha')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <th scope='row'>
                                            {t(k('table.sociedades'), 'Sociedades, calendario anterior')}
                                        </th>
                                        <td>{t(k('table.sociedades.date'), '1 de enero de 2027')}</td>
                                    </tr>
                                    <tr>
                                        <th scope='row'>
                                            {t(k('table.autonomos'), 'Autónomos y resto, calendario anterior')}
                                        </th>
                                        <td>{t(k('table.autonomos.date'), '1 de julio de 2027')}</td>
                                    </tr>
                                    <tr>
                                        <th scope='row'>
                                            <strong>
                                                {t(k('table.todos'), 'Todos, según la nota del 5 de octubre')}
                                            </strong>
                                        </th>
                                        <td>
                                            <strong>{t(k('table.todos.date'), 'Octubre de 2028')}</strong>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div className={article.prose}>
                            <p className={article.p}>
                                <strong>
                                    {t(
                                        k('hacienda.p2.strong'),
                                        'La nueva fecha de Verifactu es octubre de 2028 para todos.',
                                    )}
                                </strong>{' '}
                                {t(
                                    k('hacienda.p2.before'),
                                    'Es la misma en la que llega la factura electrónica obligatoria para quien factura hasta 8 millones de euros al año, ',
                                )}
                                <span className={article.cite}>
                                    <a
                                        href={REF.efe.href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className={article.link}
                                    >
                                        {t(k('hacienda.p2.link'), 'según recogió EFE el 5 de octubre de 2026')}
                                    </a>
                                    {'.'}
                                    <SourceChip source='efe' />
                                </span>
                            </p>
                            <p className={article.p}>
                                {t(k('hacienda.p3.before'), 'La fecha original era 2025. El ')}
                                <span className={article.cite}>
                                    <a
                                        href={REF.rd254.href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className={article.link}
                                    >
                                        {t(k('hacienda.p3.rd254'), 'Real Decreto 254/2025')}
                                    </a>
                                    <SourceChip source='rd254' />
                                </span>
                                {t(k('hacienda.p3.mid'), ' la llevó a 2026 y el ')}
                                <span className={article.cite}>
                                    <a
                                        href={REF.rdl15.href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className={article.link}
                                    >
                                        {t(k('hacienda.p3.rdl15'), 'Real Decreto-ley 15/2025')}
                                    </a>
                                    <SourceChip source='rdl15' />
                                </span>
                                {t(k('hacienda.p3.after'), ', a 2027.')}
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                <strong>
                                    {t(k('hacienda.p4'), 'Es la tercera vez que se mueve la fecha de Verifactu.')}
                                </strong>
                            </p>
                        </div>
                    </section>

                    <section id='verifactu-ya-no-es-obligatorio'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.obligatorio'), '¿Verifactu ya no es obligatorio?')}
                        </h2>
                        <div className={article.prose}>
                            <p className={article.p}>
                                {t(
                                    k('obligatorio.p1'),
                                    'Lo es. Lo que ha cambiado es la fecha, y ni siquiera eso está cerrado.',
                                )}
                            </p>
                            <p className={article.p}>
                                <strong>
                                    {t(
                                        k('obligatorio.p2.strong'),
                                        'A 6 de octubre de 2026, el retraso a 2028 es una nota informativa, no una norma publicada en el BOE.',
                                    )}
                                </strong>{' '}
                                {t(
                                    k('obligatorio.p2.after'),
                                    'Hasta que se publique, el calendario que figura en la ley sigue siendo el de 2027.',
                                )}
                            </p>
                            <p className={article.p}>
                                {t(
                                    k('obligatorio.p3.before'),
                                    'Y hay algo que no se ha movido nunca. La Ley Antifraude de 2021 añadió el ',
                                )}
                                <span className={article.cite}>
                                    <a
                                        href={REF.lgt.href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className={article.link}
                                    >
                                        {t(k('obligatorio.p3.link'), 'artículo 201 bis a la Ley General Tributaria')}
                                    </a>
                                    <SourceChip source='lgt' />
                                </span>
                                {t(
                                    k('obligatorio.p3.after'),
                                    ', que sanciona tener o usar programas que permitan llevar una caja B, ocultar ventas o modificar tickets ya cobrados.',
                                )}
                            </p>
                            <p className={article.p}>
                                <strong>
                                    {t(
                                        k('obligatorio.p4'),
                                        'La sanción es de hasta 50.000 euros por ejercicio, y el aplazamiento de Verifactu no la toca.',
                                    )}
                                </strong>
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                {t(
                                    k('obligatorio.p5'),
                                    'Dicho en la barra: si tu TPV de siempre te deja borrar un ticket sin rastro, el problema no es Verifactu. Es de antes.',
                                )}
                            </p>
                        </div>
                    </section>

                    <section id='cualquier-tpv-ya-trae-verifactu'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.compra'), 'Cualquier TPV que compres hoy ya trae Verifactu')}
                        </h2>
                        <div className={article.prose}>
                            <p className={article.p}>
                                <strong>
                                    {t(
                                        k('compra.p1.strong'),
                                        'Desde el 29 de julio de 2025, los fabricantes solo pueden venderte programas de facturación adaptados al reglamento.',
                                    )}
                                </strong>{' '}
                                {t(k('compra.p1.after'), 'Es el plazo que fijó para ellos el Real Decreto 254/2025.')}
                            </p>
                            <p className={article.p}>
                                {t(k('compra.p2'), 'Así que "con Verifactu" no es un extra: es lo mínimo.')}
                            </p>
                            <p className={article.p}>
                                {t(
                                    k('compra.p3'),
                                    'La nota de Hacienda anuncia además que, cuando se apruebe el nuevo calendario, cambiarán aspectos técnicos para acercar Verifactu a la factura electrónica y a la norma europea de IVA digital, la ViDA.',
                                )}
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                <strong>
                                    {t(
                                        k('compra.p4'),
                                        'Lo que compres con prisa este otoño puede necesitar otra actualización antes de 2028.',
                                    )}
                                </strong>
                            </p>
                        </div>
                    </section>

                    <section id='cuanto-me-ahorro'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.ahorro'), '¿Cuánto me ahorro si no cambio de TPV ahora?')}
                        </h2>
                        <div className={article.prose}>
                            <p className={article.p}>
                                {t(k('ahorro.p1'), 'Depende de lo que tengas hoy, y aquí conviene no hacerse trampas.')}
                            </p>
                            <p className={article.p}>
                                {t(
                                    k('ahorro.p2.before'),
                                    'Las tarifas públicas de software TPV para hostelería van de ',
                                )}
                                <strong>{t(k('ahorro.p2.range'), '30 a 79 euros al mes')}</strong>
                                {t(
                                    k('ahorro.p2.mid'),
                                    ', sin contar hardware. Varias marcas cobran además la puesta en marcha, ',
                                )}
                                <strong>{t(k('ahorro.p2.setup'), 'entre 250 y 280 euros')}</strong>
                                {t(k('ahorro.p2.license'), '. Una licencia de pago único ronda ')}
                                <strong>{t(k('ahorro.p2.once'), 'de 300 a 800 euros')}</strong>
                                {'.'}
                            </p>
                            <p className={cn(article.p, 'text-article-muted')}>
                                <em>
                                    {t(
                                        k('ahorro.note'),
                                        'Supuesto nuestro: un bar sociedad limitada que pensaba cambiar en noviembre, con una tarifa pública de zona media, 49 euros al mes y 280 de puesta en marcha.',
                                    )}
                                </em>
                            </p>
                        </div>
                        <div className={article.tableWrap}>
                            <table className={article.tableFacts}>
                                <thead>
                                    <tr>
                                        <th scope='col'>{t(k('ahorro.table.col.item'), 'Concepto')}</th>
                                        <th scope='col'>{t(k('ahorro.table.col.year'), 'Primer año')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <th scope='row'>{t(k('ahorro.table.setup'), 'Puesta en marcha')}</th>
                                        <td>{t(k('ahorro.table.setup.value'), '280 €')}</td>
                                    </tr>
                                    <tr>
                                        <th scope='row'>{t(k('ahorro.table.fee'), 'Cuota, 12 meses')}</th>
                                        <td>{t(k('ahorro.table.fee.value'), '588 €')}</td>
                                    </tr>
                                    <tr>
                                        <th scope='row'>
                                            <strong>{t(k('ahorro.table.saved'), 'Lo que no gastas si esperas')}</strong>
                                        </th>
                                        <td>
                                            <strong>{t(k('ahorro.table.saved.value'), '868 €')}</strong>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <div className={article.prose}>
                            <p className={article.p}>
                                <strong>
                                    {t(
                                        k('ahorro.p3'),
                                        'Si tu TPV actual es de licencia, ya está pagado y no permite ocultar ventas, aplazar el cambio te ahorra unos 870 euros este año.',
                                    )}
                                </strong>
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                {t(
                                    k('ahorro.p4'),
                                    'Si ya pagas una cuota parecida, lo único que te ahorras es la puesta en marcha.',
                                )}
                            </p>
                        </div>
                    </section>

                    <section id='lo-que-ningun-tpv-te-dice'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.tpv'), 'Lo que ningún TPV te dice')}
                        </h2>
                        <div className={article.prose}>
                            <p className={article.p}>
                                {t(k('tpv.p1'), 'Un TPV sabe lo que vendes. No sabe lo que te cuesta.')}
                            </p>
                            <p className={article.p}>
                                {t(
                                    k('tpv.p2'),
                                    'Te dice que el viernes salieron 40 raciones de croquetas. No te dice que el aceite y la leche han subido desde enero y que cada ración te deja menos que entonces.',
                                )}
                            </p>
                            <p className={article.p}>
                                <em>
                                    {t(
                                        k('tpv.note'),
                                        'Cuenta nuestra, sobre un local tipo de 300.000 euros de venta al año con un food cost del 33 %:',
                                    )}
                                </em>{' '}
                                {t(k('tpv.p3.before'), 'el género son unos 99.000 euros al año. ')}
                                <strong>
                                    {t(
                                        k('tpv.p3.strong'),
                                        'Si el food cost se va del 33 al 35 % sin que lo veas, son 6.000 euros.',
                                    )}
                                </strong>
                            </p>
                            <p className={article.p}>
                                <span className={article.cite}>
                                    {t(
                                        k('tpv.p4.before'),
                                        'Las asociaciones del sector sitúan hoy la rentabilidad de bares y restaurantes ',
                                    )}
                                    <a
                                        href={REF.marinaPlaza.href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className={article.link}
                                    >
                                        {t(k('tpv.p4.link'), 'en un 3 o un 4 % de lo que facturan')}
                                    </a>
                                    {t(
                                        k('tpv.p4.after'),
                                        '. Para ese local, el beneficio del año no pasa de 12.000 euros.',
                                    )}
                                    <SourceChip source='marinaPlaza' />
                                </span>
                            </p>
                            <p className={article.p}>
                                <strong>
                                    {t(
                                        k('tpv.p5'),
                                        'Dos puntos de food cost que nadie mira pueden llevarse la mitad del año.',
                                    )}
                                </strong>
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                {t(k('tpv.p6.before'), 'Si nunca has hecho esa cuenta, aquí está ')}
                                <LocaleLink
                                    to={learnArticlePath('como-saber-si-mi-restaurante-es-rentable')}
                                    className={article.link}
                                >
                                    {t(k('tpv.p6.link'), 'cómo saber si tu restaurante gana dinero sin comprar nada')}
                                </LocaleLink>
                                {'.'}
                            </p>
                        </div>
                    </section>

                    <section id='lo-que-cuesta-tipi'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.tipi'), 'Lo que cuesta Tipi, con todos los números')}
                        </h2>
                        <div className={article.prose}>
                            <p className={article.p}>
                                {t(k('tipi.p1'), 'Tipi es lo que vendemos, así que van los números enteros.')}
                            </p>
                            <p className={article.p}>
                                {t(k('tipi.p2.before'), 'Primero lo que Tipi no es: ')}
                                <strong>{t(k('tipi.p2.strong'), 'no es un TPV ni sustituye al tuyo.')}</strong>{' '}
                                {t(
                                    k('tipi.p2.after'),
                                    'No cobra ni emite tickets. Cuando te toque adaptarte a Verifactu, tendrás que hacerlo igual.',
                                )}
                            </p>
                            <p className={article.p}>{t(k('tipi.p3'), 'Lo que cuesta:')}</p>
                            <ul className={article.dashList}>
                                <li className={article.dashItem}>
                                    <strong>{t(k('tipi.list.1.strong'), '139 euros de cuota del primer mes')}</strong>
                                </li>
                                <li className={article.dashItem}>
                                    <strong>{t(k('tipi.list.2.strong'), '39 euros al mes')}</strong>
                                    {t(k('tipi.list.2.after'), ' a partir del segundo, con 5 albaranes incluidos.')}
                                </li>
                                <li className={article.dashItem}>
                                    <strong>{t(k('tipi.list.3.strong'), 'Cada albarán de más')}</strong>
                                    {t(k('tipi.list.3.after'), ' cuesta 0,70 euros.')}
                                </li>
                            </ul>
                            <p className={article.p}>
                                <em>
                                    {t(
                                        k('tipi.note'),
                                        'Supuesto nuestro: un bar que recibe 80 albaranes al mes, con la cuota del primer mes a 139 euros.',
                                    )}
                                </em>{' '}
                                {t(
                                    k('tipi.p4.before'),
                                    'El primer año son 568 euros de activación y cuota, más 630 de albaranes. ',
                                )}
                                <strong>{t(k('tipi.p4.strong'), 'Unos 1.200 euros en total.')}</strong>
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                <strong>
                                    {t(
                                        k('tipi.p5'),
                                        'Los 868 euros del TPV que no compras cubren unos tres cuartos de ese primer año, no todo.',
                                    )}
                                </strong>{' '}
                                {t(
                                    k('tipi.p5.after'),
                                    'El resto solo sale a cuenta si, al mirar tu carta, aparece dinero que hoy se te escapa.',
                                )}
                            </p>
                        </div>
                    </section>

                    <section id='antes-de-que-vuelva-a-llamar'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.comercial'), 'Antes de que vuelva a llamar el comercial')}
                        </h2>
                        <div className={article.prose}>
                            <NewsArticleNumList items={checklist} />
                        </div>
                    </section>

                    <section id='lo-que-falta-por-escribir-en-el-boe'>
                        <h2 className={cn(article.h2, article.h2Spaced)}>
                            {t(k('h2.boe'), 'Lo que falta por escribir en el BOE')}
                        </h2>
                        <div className={article.prose}>
                            <p className={article.p}>
                                <strong>
                                    {t(k('boe.p1.strong'), 'La fecha de octubre de 2028 no está publicada en el BOE.')}
                                </strong>{' '}
                                {t(k('boe.p1.after'), 'Hasta que lo esté, el calendario legal es el de 2027.')}
                            </p>
                            <p className={article.p}>
                                <strong>{t(k('boe.p2.strong'), 'Las Cortes están disueltas.')}</strong>{' '}
                                {t(
                                    k('boe.p2.after'),
                                    'El Gobierno ha convocado elecciones generales para el 29 de noviembre de 2026, y no se sabe cuándo ni con qué instrumento se aprobará el nuevo calendario.',
                                )}
                            </p>
                            <p className={article.p}>
                                <strong>
                                    {t(k('boe.p3.strong'), 'Los medios no coinciden en cuántos aplazamientos van.')}
                                </strong>{' '}
                                {t(
                                    k('boe.p3.after'),
                                    'Varios hablan del segundo; en el BOE, contando desde 2025, es el tercero.',
                                )}
                            </p>
                            <p className={article.p}>
                                <strong>
                                    {t(k('boe.p4.strong'), 'Las tarifas de TPV son las que publican los fabricantes')}
                                </strong>
                                {t(
                                    k('boe.p4.after'),
                                    ', sin hardware ni terminales extra. Tu presupuesto real puede ser bastante mayor, y los dos supuestos de este artículo son nuestros.',
                                )}
                            </p>
                            <p className={article.p}>
                                {t(
                                    k('boe.p5'),
                                    'Si tenías presupuesto para un TPV nuevo solo por Verifactu y no sabes cuánto te cuesta cada plato, empieza por ahí: cuéntanoslo y le ponemos números.',
                                )}
                            </p>
                            <p className={cn(article.p, article.pGap2xl)}>
                                {t(
                                    k('boe.p6'),
                                    'Y cuando vuelva a llamar el del TPV, que llamará, ya sabrás qué preguntarle.',
                                )}
                            </p>
                        </div>
                    </section>
                </ArticleReadingLayout>

                <section className={article.cta}>
                    <h2 className={cn(article.h2, article.h2Cta)}>
                        {t(k('h2.cta'), 'Si el presupuesto del TPV se ha quedado suelto')}
                    </h2>
                    <p className={article.ctaSub}>
                        {t(
                            k('cta.sub'),
                            'Cuéntanoslo y le ponemos números a lo que te cuesta cada plato. Tipi no sustituye tu TPV: mira el coste que el TPV no ve.',
                        )}
                    </p>
                    <div className={article.ctaActionsCenter}>
                        <a
                            href={contactWhatsapp}
                            target='_blank'
                            rel='noopener noreferrer'
                            className={cn(article.btn, article.btnSolid)}
                        >
                            {t(k('cta.whatsapp'), 'Escríbenos por WhatsApp')}
                        </a>
                    </div>
                </section>

                <section className={article.sources} aria-labelledby='fuentes-heading'>
                    <h2 id='fuentes-heading' className={article.sourcesTitle}>
                        {t(k('sourcesTitle'), 'Fuentes')}
                    </h2>
                    <ul className={article.sourcesList}>
                        {sources.map(source => (
                            <li key={source.href}>
                                <a
                                    href={source.href}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className={article.link}
                                >
                                    {source.label}
                                </a>
                            </li>
                        ))}
                        {ownSources.map(label => (
                            <li key={label}>{label}</li>
                        ))}
                    </ul>
                </section>
            </article>
        </div>
    )
}
